import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AdminBilling from "@/components/AdminBilling";
import AdminClaims from "@/components/AdminClaims";
import WeeklyEmails from "@/components/WeeklyEmails";
import ContractTab from "@/components/ContractTab";
import DocumentsTab from "@/components/DocumentsTab";
import {
  Search, Building2, ShieldAlert, FolderOpen, FileSignature, Receipt, Mail, Users as UsersIcon,
  LayoutDashboard, Eye, Maximize2, Minimize2, ChevronRight, CheckCircle2, XCircle, ArrowRight,
  SlidersHorizontal, ArrowLeft, Store,
} from "lucide-react";

const TABS = [
  { v: "overview", l: "Overview", icon: LayoutDashboard },
  { v: "billing", l: "Monthly bill", icon: Receipt },
  { v: "pricing", l: "Rate card", icon: SlidersHorizontal },
  { v: "contract", l: "Contract", icon: FileSignature },
  { v: "claims", l: "Claims", icon: ShieldAlert },
  { v: "documents", l: "Documents", icon: FolderOpen },
  { v: "emails", l: "Emails", icon: Mail },
  { v: "users", l: "Users", icon: UsersIcon },
];

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const CONTRACT_META = {
  active: { l: "Active", cls: "bg-emerald-100 text-emerald-700" },
  pending: { l: "Pending", cls: "bg-amber-100 text-amber-800" },
  expired: { l: "Expired", cls: "bg-red-100 text-red-700" },
  none: { l: "No contract", cls: "bg-slate-100 text-slate-500" },
};

function Stat({ label, value, tone = "slate", testId }) {
  const tones = { slate: "text-slate-900", orange: "text-orange-600", red: "text-red-600", green: "text-emerald-600", blue: "text-blue-600" };
  return (
    <div data-testid={testId}>
      <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold ${tones[tone]}`} style={{ fontFamily: "Manrope" }}>{value}</p>
    </div>
  );
}

export default function OpsConsole({ accounts = [], users = [], onEditUser, fixedAccountId = null, tab: tabProp = null, onTabChange = null, onEditAccount = null }) {
  const navigate = useNavigate();
  const { setViewAs, can } = useAuth();
  const canBilling = can("billing", "view");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(fixedAccountId);
  const [ownTab, setOwnTab] = useState("overview");
  const tab = tabProp ?? ownTab;
  const setTab = onTabChange ?? setOwnTab;
  const [claimsByAccount, setClaimsByAccount] = useState({});
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotError, setSnapshotError] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => { if (fixedAccountId) setSelectedId(fixedAccountId); }, [fixedAccountId]);

  const loadSnapshot = (id) => {
    if (!id) { setSnapshot(null); return; }
    setSnapshotError(false);
    api.get(`/admin/accounts/${id}/snapshot`).then(({ data }) => setSnapshot(data)).catch(() => { setSnapshot(null); setSnapshotError(true); });
  };

  useEffect(() => {
    api.get("/claims").then(({ data }) => {
      const m = {};
      (data.claims || []).forEach((c) => {
        if (["credit_applied", "denied"].includes(c.status)) return;
        m[c.account_id] = (m[c.account_id] || 0) + 1;
      });
      setClaimsByAccount(m);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedId) { setSnapshot(null); return; }
    setSnapshot(null);
    loadSnapshot(selectedId);
  }, [selectedId]);

  const selected = useMemo(() => accounts.find((a) => a.id === selectedId) || null, [accounts, selectedId]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return accounts;
    return accounts.filter((a) =>
      (a.company_name || "").toLowerCase().includes(t) ||
      (a.boxhero_account_id || "").toLowerCase().includes(t) ||
      (a.primary_email || "").toLowerCase().includes(t)
    );
  }, [accounts, q]);

  const accountUsers = useMemo(() => users.filter((u) => u.account_id === selectedId), [users, selectedId]);

  const shell = (
    <div className="flex flex-col lg:flex-row bg-white border border-slate-200 rounded-xl overflow-hidden" style={{ minHeight: fullscreen ? "100%" : (fixedAccountId ? undefined : "70vh"), height: fullscreen ? "100%" : undefined }} data-testid="ops-console">
      {/* Left master list */}
      {!fixedAccountId && (
      <div className="lg:w-80 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col">
        <div className="p-3 border-b border-slate-100">
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Customers</h2>
            <button onClick={() => setFullscreen((f) => !f)} data-testid="ops-fullscreen-toggle" className="text-slate-400 hover:text-blue-600 p-1" title={fullscreen ? "Exit full screen" : "Expand to full screen"}>
              {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="ops-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…"
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5" style={{ maxHeight: fullscreen ? "calc(100vh - 160px)" : "62vh" }}>
          {filtered.map((a) => {
            const oc = claimsByAccount[a.id] || 0;
            const active = a.id === selectedId;
            return (
              <button key={a.id} data-testid={`ops-account-${a.id}`} onClick={() => { setSelectedId(a.id); setTab("overview"); }}
                className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-all ${active ? "border-blue-400 bg-blue-50/60 ring-1 ring-blue-200" : "border-transparent hover:bg-slate-50"}`}>
                <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${active ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"}`}>{initials(a.company_name)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-900 truncate">{a.company_name}</span>
                  <span className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-slate-400 font-mono">{a.boxhero_account_id}</span>
                    {oc > 0 && <span className="text-[10px] font-semibold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full">{oc} open</span>}
                  </span>
                </span>
                <ChevronRight className={`w-4 h-4 shrink-0 ${active ? "text-blue-500" : "text-slate-300"}`} />
              </button>
            );
          })}
          {filtered.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No customers match.</p>}
        </div>
      </div>
      )}

      {/* Right detail */}
      <div className="flex-1 min-w-0 overflow-y-auto" style={{ maxHeight: fullscreen ? "calc(100vh - 90px)" : (fixedAccountId ? undefined : "72vh") }}>
        {!selected ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-12 text-slate-400" data-testid="ops-empty">
            <Building2 className="w-12 h-12 mb-3 text-slate-300" />
            <p className="text-lg font-semibold text-slate-500">Pick a customer</p>
            <p className="text-sm">Select a customer on the left to manage their billing, contract, claims, documents and emails.</p>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            {/* Header */}
            {!fixedAccountId && (
            <button
              data-testid="ops-back-to-list"
              onClick={() => { setSelectedId(null); setTab("overview"); }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600 mb-3"
            >
              <ArrowLeft className="w-4 h-4" /> All customers
            </button>
            )}
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{selected.company_name}</h1>
                  {snapshot && <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CONTRACT_META[snapshot.contract_status]?.cls || CONTRACT_META.none.cls}`}>{CONTRACT_META[snapshot.contract_status]?.l || "No contract"}</span>}
                </div>
                <p className="text-sm text-slate-400 font-mono mt-1">{selected.boxhero_account_id} · {selected.primary_email}</p>
              </div>
              <div className="flex items-center gap-2">
                {onEditAccount && (
                  <button data-testid="ops-edit-account" onClick={onEditAccount}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-blue-700 border border-slate-200 hover:border-blue-300 px-3 py-2 rounded-md">
                    <Store className="w-4 h-4" /> Account &amp; stores
                  </button>
                )}
                {(selected.shipstation_store_id || selected.shipstation_store_ids?.length || selected.stripe_customer_id) && (
                  <button data-testid="ops-view-as" onClick={() => { setViewAs(selected.id, selected.company_name); navigate("/"); }}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-orange-600 border border-slate-200 hover:border-orange-300 px-3 py-2 rounded-md">
                    <Eye className="w-4 h-4" /> View as
                  </button>
                )}
              </div>
            </div>

            {/* KPI strip */}
            {snapshotError && (
              <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-2 mb-3 text-sm text-red-700" data-testid="ops-snapshot-error">
                Couldn't load this customer's stats.
                <button onClick={() => loadSnapshot(selected.id)} className="font-semibold underline">Retry</button>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50 border border-slate-100 rounded-xl px-5 py-4 mb-5">
              <Stat label="Open claims" value={snapshot ? snapshot.open_claims : "—"} tone={snapshot?.open_claims ? "orange" : "green"} testId="ops-kpi-claims" />
              <Stat label="Documents" value={snapshot ? `${snapshot.shared_documents}/${snapshot.documents_count}` : "—"} tone="blue" testId="ops-kpi-docs" />
              <Stat label="Users" value={snapshot ? snapshot.users_count : "—"} testId="ops-kpi-users" />
              <Stat label="Returns" value={snapshot ? snapshot.returns_count : "—"} testId="ops-kpi-returns" />
            </div>

            {/* Tabs — hidden when the console rail is already driving them */}
            {!onTabChange && (
            <div className="flex items-center gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
              {TABS.filter((t) => !["billing", "pricing"].includes(t.v) || canBilling).map((t) => (
                <button key={t.v} data-testid={`ops-tab-${t.v}`} onClick={() => setTab(t.v)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.v ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                  <t.icon className="w-4 h-4" /> {t.l}
                </button>
              ))}
            </div>
            )}

            {/* Tab content */}
            <div data-testid={`ops-panel-${tab}`}>
              {tab === "overview" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <h3 className="text-base font-bold text-slate-900 mb-3">Integrations</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start justify-between gap-3"><span className="text-slate-600">ShipStation stores</span>{(selected.shipstation_store_ids?.length ? selected.shipstation_store_ids : (selected.shipstation_store_id ? [selected.shipstation_store_id] : [])).length ? <span className="inline-flex flex-wrap justify-end gap-1 text-emerald-600 font-semibold"><CheckCircle2 className="w-4 h-4 shrink-0" /> {(selected.shipstation_store_ids?.length ? selected.shipstation_store_ids : [selected.shipstation_store_id]).join(", ")}</span> : <span className="inline-flex items-center gap-1 text-red-500 font-semibold"><XCircle className="w-4 h-4" /> Not linked</span>}</li>
                      <li className="flex items-center justify-between"><span className="text-slate-600">Stripe customer</span>{selected.stripe_customer_id ? <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><CheckCircle2 className="w-4 h-4" /> Linked</span> : <span className="inline-flex items-center gap-1 text-red-500 font-semibold"><XCircle className="w-4 h-4" /> Not linked</span>}</li>
                    </ul>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <h3 className="text-base font-bold text-slate-900 mb-3">Quick actions</h3>
                    <div className="space-y-2">
                      {[["billing", "Review this month's bill"], ["pricing", "Set the rate card"], ["contract", "Update contract terms"], ["documents", "Share a document"], ["emails", "Send a business update"]].filter(([v]) => !["billing", "pricing"].includes(v) || canBilling).map(([v, l]) => (
                        <button key={v} onClick={() => setTab(v)} className="w-full flex items-center justify-between text-sm text-slate-700 hover:text-blue-700 hover:bg-blue-50/50 rounded-lg px-3 py-2 border border-slate-100">
                          {l} <ArrowRight className="w-4 h-4" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {tab === "billing" && (canBilling
                ? <AdminBilling accounts={accounts} fixedAccountId={selected.id} view="monthly" />
                : <div className="text-sm text-slate-500 py-8 text-center">Billing is restricted — ask a super admin to grant you access.</div>)}
              {tab === "pricing" && (canBilling
                ? <AdminBilling accounts={accounts} fixedAccountId={selected.id} view="rate_card" />
                : <div className="text-sm text-slate-500 py-8 text-center">Pricing is restricted — ask a super admin to grant you access.</div>)}
              {tab === "contract" && <ContractTab accountId={selected.id} />}
              {tab === "claims" && <AdminClaims accountId={selected.id} />}
              {tab === "documents" && <DocumentsTab accountId={selected.id} onChanged={() => loadSnapshot(selected.id)} />}
              {tab === "emails" && <WeeklyEmails accounts={accounts} fixedAccountId={selected.id} />}
              {tab === "users" && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="ops-users-table">
                  <table className="w-full text-left text-sm">
                    <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                      <th className="py-2.5 px-4 font-medium">Name</th><th className="py-2.5 px-4 font-medium">Email</th><th className="py-2.5 px-4 font-medium">Role</th><th className="py-2.5 px-4 font-medium">Status</th><th className="py-2.5 px-4 font-medium text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                      {accountUsers.map((u) => (
                        <tr key={u.id} className="border-b border-slate-100">
                          <td className="py-2.5 px-4 font-medium text-slate-800">{u.name}</td>
                          <td className="py-2.5 px-4 text-slate-600">{u.email}</td>
                          <td className="py-2.5 px-4"><span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">{u.role}</span></td>
                          <td className="py-2.5 px-4 text-slate-500">{u.status}</td>
                          <td className="py-2.5 px-4 text-right">{onEditUser && <button onClick={() => onEditUser(u)} data-testid={`ops-edit-user-${u.id}`} className="text-blue-600 hover:text-blue-700 text-xs font-semibold">Edit</button>}</td>
                        </tr>
                      ))}
                      {accountUsers.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No users linked to this account.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[90] bg-slate-100 p-4 overflow-hidden" data-testid="ops-console-fullscreen">
        {shell}
      </div>
    );
  }
  return shell;
}
